import { checkPermissions, useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { Spinner, Tabs, TabsList, TabsTrigger } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbFileDescription } from "react-icons/tb";
import z from "zod";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { PERMISSIONS } from "@/constants/permissions";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { fadeInUp, transitions } from "@/utils/animations";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { PrpReportsPanel } from "./reports/components/prp-reports-panel";
import { SalesSummariesPanel } from "./reports/components/sales-summaries-panel";
import { REPORT_SURFACE_CLASS } from "./reports/constants/report-ui";
import {
    REPORT_MAIN_CATEGORY_IDS,
    REPORT_VIEW_IDS,
    type ReportMainCategoryId,
    type ReportViewId,
    SALES_SUMMARY_PERIOD_IDS,
    type SalesSummaryPeriodId,
} from "./reports/types";
import { findPrpTeam, getPrpTeamAgents } from "./reports/utils/prp-team-members";

const REPORT_PAGE_MAX_WIDTH_CLASS = "max-w-5xl";
const MAIN_CATEGORY_TAB_LIST_CLASS =
    "grid h-auto w-full grid-cols-2 gap-1 rounded-xl border border-border/60 bg-muted/40 p-1";
const MAIN_CATEGORY_TAB_TRIGGER_CLASS =
    "rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm";

const ReportsSearchSchema = z.object({
    category: z.enum([REPORT_MAIN_CATEGORY_IDS.PRP, REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES]).optional(),
    period: z
        .enum([SALES_SUMMARY_PERIOD_IDS.DAILY, SALES_SUMMARY_PERIOD_IDS.WEEKLY, SALES_SUMMARY_PERIOD_IDS.MONTHLY])
        .optional(),
    agent: z.string().optional(),
    dailyDate: z.string().optional(),
    weeklyDate: z.string().optional(),
    month: z.string().optional(),
    prpDate: z.string().optional(),
    prpView: z.string().optional(),
});

export type ReportsSearch = z.infer<typeof ReportsSearchSchema>;

function isReportMainCategoryId(value: string): value is ReportMainCategoryId {
    return value === REPORT_MAIN_CATEGORY_IDS.PRP || value === REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES;
}

function isReportViewId(value: string): value is ReportViewId {
    const reportTabs: ReportViewId[] = [
        REPORT_VIEW_IDS.FIRST_HALF,
        REPORT_VIEW_IDS.SECOND_HALF,
        REPORT_VIEW_IDS.DAILY,
        REPORT_VIEW_IDS.WEEKLY,
    ];
    return reportTabs.some((tab) => tab === value);
}

function resolveReportsView(
    search: ReportsSearch,
    availableCategories: ReportMainCategoryId[]
): {
    mainCategory: ReportMainCategoryId;
    salesPeriod: SalesSummaryPeriodId;
} {
    const defaultCategory = availableCategories[0] ?? REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES;
    const defaultPeriod = search.period ?? SALES_SUMMARY_PERIOD_IDS.WEEKLY;
    const requestedCategory = search.category;

    if (!requestedCategory) {
        return {
            mainCategory: defaultCategory,
            salesPeriod: defaultPeriod,
        };
    }

    if (
        requestedCategory === REPORT_MAIN_CATEGORY_IDS.PRP &&
        availableCategories.includes(REPORT_MAIN_CATEGORY_IDS.PRP)
    ) {
        return {
            mainCategory: REPORT_MAIN_CATEGORY_IDS.PRP,
            salesPeriod: defaultPeriod,
        };
    }

    if (requestedCategory === REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES) {
        if (!availableCategories.includes(REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES)) {
            return {
                mainCategory: defaultCategory,
                salesPeriod: defaultPeriod,
            };
        }

        return {
            mainCategory: REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES,
            salesPeriod: defaultPeriod,
        };
    }

    return {
        mainCategory: defaultCategory,
        salesPeriod: defaultPeriod,
    };
}

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_REPORTS)({
    component: PerformanceReportsPage,
    validateSearch: zodValidator(ReportsSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "reports",
                to: APP_ROUTES_TO.PERFORMANCE_REPORTS,
            },
            sidebar: {
                label: "reports",
                icon: TbFileDescription,
            },
            title: "reports",
        },
        permissions: [PERMISSIONS.CALL_REPORTS_PRP_ADMIN, PERMISSIONS.CALL_REPORTS_SALES_ADMIN],
    },
});

function PerformanceReportsPage() {
    const { t } = useTranslation();
    const { userData } = useAuth();
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();
    const { data: teamsResponse, isLoading: isLoadingTeams } = useTeams();

    const [lastSelectedAgentId] = useLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.SELECTED_AGENT_REPORTS.key,
        LOCAL_STORAGE_KEYS.SELECTED_AGENT_REPORTS.default
    );

    const selectedSalesAgentId = searchParams.agent ?? lastSelectedAgentId ?? undefined;

    const permissionCodes = userData?.permissionCodes ?? [];

    const hasPrpReportsPermission = useMemo(
        () => checkPermissions(permissionCodes, [PERMISSIONS.CALL_REPORTS_PRP_ADMIN]),
        [permissionCodes]
    );

    const hasSalesReportsPermission = useMemo(
        () => checkPermissions(permissionCodes, [PERMISSIONS.CALL_REPORTS_SALES_ADMIN]),
        [permissionCodes]
    );

    const teams = teamsResponse?.data ?? [];
    const prpTeam = useMemo(() => findPrpTeam(teams), [teams]);
    const prpTeamAgents = useMemo(() => getPrpTeamAgents(teams), [teams]);

    const availableCategories = useMemo((): ReportMainCategoryId[] => {
        const categories: ReportMainCategoryId[] = [];

        if (hasPrpReportsPermission) {
            categories.push(REPORT_MAIN_CATEGORY_IDS.PRP);
        }

        if (hasSalesReportsPermission) {
            categories.push(REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES);
        }

        return categories;
    }, [hasPrpReportsPermission, hasSalesReportsPermission]);

    const { mainCategory, salesPeriod } = useMemo(
        () => resolveReportsView(searchParams, availableCategories),
        [searchParams, availableCategories]
    );

    const prpReportDate = useMemo(() => {
        if (!searchParams.prpDate) {
            return undefined;
        }
        const parsed = DateTime.fromISO(searchParams.prpDate);
        return parsed.isValid ? parsed : undefined;
    }, [searchParams.prpDate]);

    const prpReportView = useMemo(() => {
        if (searchParams.prpView && typeof searchParams.prpView === "string" && isReportViewId(searchParams.prpView)) {
            return searchParams.prpView;
        }
        return undefined;
    }, [searchParams.prpView]);

    const salesDailyDate = useMemo(() => {
        if (!searchParams.dailyDate) {
            return undefined;
        }
        const parsed = DateTime.fromISO(searchParams.dailyDate);
        return parsed.isValid ? parsed : undefined;
    }, [searchParams.dailyDate]);

    const salesWeeklyDate = useMemo(() => {
        if (!searchParams.weeklyDate) {
            return undefined;
        }
        const parsed = DateTime.fromISO(searchParams.weeklyDate);
        return parsed.isValid ? parsed : undefined;
    }, [searchParams.weeklyDate]);

    const salesMonth = useMemo(() => {
        if (!searchParams.month) {
            return undefined;
        }
        const parsed = DateTime.fromISO(searchParams.month);
        return parsed.isValid ? parsed : undefined;
    }, [searchParams.month]);

    const buildSalesSummariesSearch = useCallback(
        (overrides: Record<string, unknown> = {}) => {
            const searchObj: Record<string, unknown> = {
                category: REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES,
                period: salesPeriod,
                ...overrides,
            };

            if (searchParams.agent && typeof searchParams.agent === "string") {
                searchObj.agent = searchParams.agent;
            } else if (selectedSalesAgentId) {
                searchObj.agent = selectedSalesAgentId;
            }

            if (salesDailyDate && searchObj.dailyDate === undefined) {
                searchObj.dailyDate = salesDailyDate.toISODate();
            }
            if (salesWeeklyDate && searchObj.weeklyDate === undefined) {
                searchObj.weeklyDate = salesWeeklyDate.toISODate();
            }
            if (salesMonth && searchObj.month === undefined) {
                searchObj.month = salesMonth.toISODate();
            }

            return searchObj;
        },
        [salesPeriod, searchParams.agent, selectedSalesAgentId, salesDailyDate, salesWeeklyDate, salesMonth]
    );

    const handlePrpDateChange = useCallback(
        (date: DateTime) => {
            const searchObj: Record<string, unknown> = {
                category: REPORT_MAIN_CATEGORY_IDS.PRP,
                prpDate: date.toISODate(),
            };
            if (prpReportView) {
                searchObj.prpView = prpReportView;
            }
            navigate({
                to: Route.to,
                search: searchObj,
                replace: true,
            });
        },
        [navigate, prpReportView]
    );

    const handlePrpViewChange = useCallback(
        (view: ReportViewId) => {
            const searchObj: Record<string, unknown> = {
                category: REPORT_MAIN_CATEGORY_IDS.PRP,
                prpView: view,
            };
            if (prpReportDate) {
                searchObj.prpDate = prpReportDate.toISODate();
            }
            navigate({
                to: Route.to,
                search: searchObj,
                replace: true,
            });
        },
        [navigate, prpReportDate]
    );

    const handleSalesDailyDateChange = useCallback(
        (date: DateTime) => {
            navigate({
                to: Route.to,
                search: buildSalesSummariesSearch({
                    period: SALES_SUMMARY_PERIOD_IDS.DAILY,
                    dailyDate: date.toISODate(),
                }),
                replace: true,
            });
        },
        [navigate, buildSalesSummariesSearch]
    );

    const handleSalesWeeklyDateChange = useCallback(
        (date: DateTime) => {
            navigate({
                to: Route.to,
                search: buildSalesSummariesSearch({
                    period: SALES_SUMMARY_PERIOD_IDS.WEEKLY,
                    weeklyDate: date.toISODate(),
                }),
                replace: true,
            });
        },
        [navigate, buildSalesSummariesSearch]
    );

    const handleSalesMonthChange = useCallback(
        (month: DateTime) => {
            navigate({
                to: Route.to,
                search: buildSalesSummariesSearch({
                    period: SALES_SUMMARY_PERIOD_IDS.MONTHLY,
                    month: month.toISODate(),
                }),
                replace: true,
            });
        },
        [navigate, buildSalesSummariesSearch]
    );

    const handleMainCategoryChange = useCallback(
        (value: string) => {
            if (!isReportMainCategoryId(value)) {
                return;
            }

            if (value === REPORT_MAIN_CATEGORY_IDS.PRP) {
                navigate({
                    to: Route.to,
                    search: {
                        category: REPORT_MAIN_CATEGORY_IDS.PRP,
                        ...(prpReportDate ? { prpDate: prpReportDate.toISODate() } : {}),
                        ...(prpReportView ? { prpView: prpReportView } : {}),
                    },
                    replace: true,
                });
                return;
            }

            navigate({
                to: Route.to,
                search: buildSalesSummariesSearch(),
                replace: true,
            });
        },
        [navigate, buildSalesSummariesSearch, prpReportDate, prpReportView]
    );

    if (isLoadingTeams) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    const showMainCategoryTabs = hasPrpReportsPermission && hasSalesReportsPermission;

    return (
        <div className={`mx-auto flex w-full ${REPORT_PAGE_MAX_WIDTH_CLASS} flex-col gap-6 px-1 py-1 sm:px-2 sm:py-2`}>
            {showMainCategoryTabs ? (
                <section className={`${REPORT_SURFACE_CLASS} p-4 sm:p-5`}>
                    <Tabs value={mainCategory} onValueChange={handleMainCategoryChange}>
                        <TabsList className={MAIN_CATEGORY_TAB_LIST_CLASS}>
                            {hasPrpReportsPermission ? (
                                <TabsTrigger
                                    value={REPORT_MAIN_CATEGORY_IDS.PRP}
                                    className={MAIN_CATEGORY_TAB_TRIGGER_CLASS}
                                >
                                    {t("performance_reports_category_prp")}
                                </TabsTrigger>
                            ) : null}
                            {hasSalesReportsPermission ? (
                                <TabsTrigger
                                    value={REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES}
                                    className={MAIN_CATEGORY_TAB_TRIGGER_CLASS}
                                >
                                    {t("performance_reports_category_sales_summaries")}
                                </TabsTrigger>
                            ) : null}
                        </TabsList>
                    </Tabs>
                </section>
            ) : null}

            <AnimatePresence mode="wait">
                <motion.div
                    key={`${mainCategory}-${salesPeriod}`}
                    {...fadeInUp}
                    transition={transitions.smooth}
                    className="flex flex-col gap-6"
                >
                    {mainCategory === REPORT_MAIN_CATEGORY_IDS.PRP && hasPrpReportsPermission && prpTeam ? (
                        <PrpReportsPanel
                            prpTeam={prpTeam}
                            prpTeamAgents={prpTeamAgents}
                            selectedReportDate={prpReportDate ?? DateTime.now().startOf("day")}
                            selectedReportView={prpReportView ?? REPORT_VIEW_IDS.FIRST_HALF}
                            onReportDateChange={handlePrpDateChange}
                            onReportViewChange={handlePrpViewChange}
                        />
                    ) : null}
                    {mainCategory === REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES && hasSalesReportsPermission ? (
                        <SalesSummariesPanel
                            activePeriod={salesPeriod}
                            selectedAgentId={selectedSalesAgentId}
                            selectedDailyDate={salesDailyDate}
                            selectedWeeklyDate={salesWeeklyDate}
                            selectedMonth={salesMonth}
                            onDailyDateChange={handleSalesDailyDateChange}
                            onWeeklyDateChange={handleSalesWeeklyDateChange}
                            onMonthChange={handleSalesMonthChange}
                        />
                    ) : null}
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
